import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  AttachFile,
  Delete,
  CheckCircle,
  ArrowForward,
  Visibility,
} from '@mui/icons-material';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import Navigation from '../Shared/Navigation.tsx';
import { getAnnualPlanningPeriodState } from '../availabilityRules';
import { shouldShowSelfAssessmentResultsStep } from './resultsStepAvailability';
import { useTranslation } from '@/core/i18n/i18n';
import { useAnyLoading } from '@/core/useAnyLoading';
import {
  Activity,
  ActivityTypes,
  Focus,
  FocusRequirementWithDetails,
  Objective,
  PlanValidation,
  TimePeriodData,
} from '@shared/schema';
interface EnterComplianceProps {
  objectives: Objective[];
  activities: Activity[];
  requirements: FocusRequirementWithDetails[];
  focuses: Focus[];
  currentVersion: number | null;
  currentPlanId: number | null;
  currentFulfillmentVersion: number | null;
  readOnlyMode: boolean | null;
  currentBusinessUnit?: string | null;
  fetchAnnualPlanStep2Context: (
    versionId: number,
    params?: { planId?: number; year?: number },
  ) => Promise<unknown>;
  fetchAnnualPlanComplianceContext: (
    versionId: number | null | undefined,
    fulfillmentVersionId: number,
  ) => Promise<unknown>;
  updateActivityProgress: (
    data: { activityId: number; progress: number }[],
    fulfillmentId: number,
  ) => Promise<void>;
  uploadActivityEvidence: (
    activityId: number,
    files: File[],
    fulfillmentId: number,
  ) => Promise<string[]>;
  deleteActivityEvidence: (activityId: number, fulfillmentId: number) => Promise<void>;
  getActivityEvidence: (
    activityId: number,
    fulfillmentId: number,
  ) => Promise<{ name: string; url: string }[]>;
  deleteActivityEvidenceFile: (
    activityId: number,
    fileName: string,
    fulfillmentId: number,
  ) => Promise<boolean | void>;
  createFulfillmentVersion: (versionId: number) => Promise<void>;
  reopenPlanVersion?: (versionId: number) => Promise<{ newVersionId?: number } | void>;
  setCurrentVersion?: (versionId: number | null) => void;
  selectedYear: string | number | null;
  scopeName: string;
  periods: TimePeriodData[];
  planValidation?: PlanValidation | null | undefined;
  getPlanValidation: (
    fulfillmentVersionId: number,
  ) => Promise<PlanValidation | null | undefined> | PlanValidation | null | undefined;
}

const EnterCompliance: React.FC<EnterComplianceProps> = ({
  focuses,
  objectives,
  activities,
  requirements,
  currentFulfillmentVersion,
  readOnlyMode,
  currentBusinessUnit,
  fetchAnnualPlanStep2Context,
  fetchAnnualPlanComplianceContext,
  updateActivityProgress,
  currentVersion,
  currentPlanId,
  uploadActivityEvidence,
  deleteActivityEvidence,
  getActivityEvidence,
  deleteActivityEvidenceFile,
  createFulfillmentVersion,
  reopenPlanVersion,
  setCurrentVersion,
  selectedYear,
  scopeName,
  periods = [],
  planValidation,
  getPlanValidation,
}) => {
  const isAnyLoading = useAnyLoading();
  const { t, tr } = useTranslation();
  const navigate = useNavigate();
  const [expandedFocus, setExpandedFocus] = useState<number | false>(false);
  const [objectivesByFocus, setObjectivesByFocus] = useState<{
    [key: number]: Objective[];
  }>({});
  const [expandedObjective, setExpandedObjective] = useState<number | false>(false);
  const [activitiesByObjective, setActivitiesByObjective] = useState<{
    [key: string]: Activity[];
  }>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(
    currentFulfillmentVersion ? false : true,
  );
  const [complianceByActivity, setComplianceByActivity] = useState<
    { activityId: number; progress: number }[]
  >([]);
  const [evidenceViewer, setEvidenceViewer] = useState<{
    open: boolean;
    activityId?: number;
    files: { name: string; url: string }[];
  }>({ open: false, files: [] });
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [progressError, setProgressError] = useState<string | null>(null);

  const definitionWindowActive = useMemo(
    () => getAnnualPlanningPeriodState(periods).definitionActionPeriodActive,
    [periods],
  );

  const definitionsWarningText = definitionWindowActive
    ? tr('enterCompliance.definitionsChangeWarning')
    : tr('enterCompliance.definitionsChangeClosed');

  useEffect(() => {
    if (readOnlyMode && currentFulfillmentVersion && !planValidation) {
      getPlanValidation(currentFulfillmentVersion);
    }
  }, [currentFulfillmentVersion, getPlanValidation, planValidation, readOnlyMode]);

  useEffect(() => {
    const versionId = currentVersion;
    if (!versionId) {
      return;
    }
    if (currentFulfillmentVersion) {
      fetchAnnualPlanComplianceContext(versionId, currentFulfillmentVersion);
      return;
    }
    const parsedYear = Number.parseInt(String(selectedYear ?? ''), 10);
    const year = Number.isInteger(parsedYear) ? parsedYear : undefined;
    fetchAnnualPlanStep2Context(versionId, {
      ...(currentPlanId ? { planId: currentPlanId } : {}),
      ...(year ? { year } : {}),
    });
  }, [
    currentVersion,
    currentFulfillmentVersion,
    fetchAnnualPlanComplianceContext,
    fetchAnnualPlanStep2Context,
    currentPlanId,
    selectedYear,
  ]);
  useEffect(() => {
    if (objectives && objectives.length > 0) {
      const objetivosAgrupados: { [key: number]: Objective[] } = {};
      objectives.forEach((objective) => {
        if (!objetivosAgrupados[objective.APFocusId]) {
          objetivosAgrupados[objective.APFocusId] = [];
        }
        (objetivosAgrupados[objective.APFocusId] ?? []).push(objective);
      });
      setObjectivesByFocus(objetivosAgrupados);
    }
  }, [objectives]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      const grouped = activities.reduce<Record<number, Activity[]>>((acc, activity) => {
        if (!acc[activity.APObjectiveId]) acc[activity.APObjectiveId] = [];
        acc[activity.APObjectiveId]!.push(activity);
        return acc;
      }, {});
      setActivitiesByObjective((prev) => {
        return { ...prev, ...grouped };
      });
    }
  }, [activities]);

  const handleFocusChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFocus(isExpanded ? panel : false);
    };

  const handleObjectiveChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedObjective(isExpanded ? panel : 0);
    };

  const handleProgressChange = (actividadId: number, value: string) => {
    const numeric = parseInt(value, 10);
    if (value === '' || isNaN(numeric) || numeric < 0 || numeric > 100) {
      setProgressError(t('enterCompliance.invalidProgressRange'));
    } else {
      setProgressError(null);
    }
    setComplianceByActivity((prev) => {
      const existingIndex = prev.findIndex((item) => item.activityId === actividadId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        if (updated[existingIndex]) {
          updated[existingIndex].progress = numeric;
        }
        return updated;
      }
      return [...prev, { activityId: actividadId, progress: numeric }];
    });
  };

  const getComplianceForActivity = (activity: Activity) => {
    return (
      complianceByActivity.find((item) => item.activityId === activity.APGroupActivityId)
        ?.progress ?? activity.CompletionProgress
    );
  };

  const parsePhotos = (raw: string | undefined | null): string[] => {
    if (!raw) return [];
    const txt = raw.trim();
    if (!txt) return [];
    if ((txt.startsWith('[') && txt.endsWith(']')) || (txt.startsWith('{') && txt.endsWith('}'))) {
      try {
        const parsed = JSON.parse(txt);
        if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string');
        if (typeof parsed === 'string') return [parsed];
      } catch {}
    }
    if (txt.includes(','))
      return txt
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    return [txt];
  };

  const allowedExtensions = useMemo(
    () => [
      'pdf',
      'png',
      'jpg',
      'jpeg',
      'gif',
      'webp',
      'bmp',
      'svg',
      'heic',
      'heif',
      'xls',
      'xlsx',
      'csv',
      'doc',
      'docx',
      'ppt',
      'pptx',
      'zip',
      'rar',
    ],
    [],
  );

  const isAllowedFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return allowedExtensions.includes(ext);
  };

  const handleFileUpload = async (
    objetivoId: number,
    actividadId: number,
    files: FileList | null,
  ) => {
    if (!files) return;
    const rawArray = Array.from(files);
    const fileArray = rawArray.filter(isAllowedFile);
    if (fileArray.length === 0) return;

    setActivitiesByObjective((prev: Record<string, Activity[]>) => ({
      ...prev,
      [objetivoId]:
        prev[objetivoId]?.map((act: Activity) =>
          act.APGroupActivityId === actividadId
            ? {
                ...act,
                evidence: [
                  ...((): (string | File)[] => {
                    if (!act.GroupActivityPhotos) return [];
                    if (
                      Array.isArray((act as unknown as { evidence?: (string | File)[] }).evidence)
                    ) {
                      return (act as unknown as { evidence: (string | File)[] }).evidence;
                    }
                    const txt = act.GroupActivityPhotos.trim();
                    if (
                      (txt.startsWith('[') && txt.endsWith(']')) ||
                      (txt.startsWith('{') && txt.endsWith('}'))
                    ) {
                      try {
                        const parsed = JSON.parse(txt);
                        if (Array.isArray(parsed)) return parsed as (string | File)[];
                        return [parsed];
                      } catch {
                        return [txt];
                      }
                    }
                    if (txt.includes(','))
                      return txt
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean);
                    return [txt];
                  })(),
                  ...fileArray,
                ],
              }
            : act,
        ) || [],
    }));

    try {
      if (uploadActivityEvidence) {
        if (!currentFulfillmentVersion) return;
        const uploaded = await uploadActivityEvidence(
          actividadId,
          fileArray,
          currentFulfillmentVersion,
        );
        setActivitiesByObjective((prev: Record<string, Activity[]>) => ({
          ...prev,
          [objetivoId]:
            prev[objetivoId]?.map((act: Activity) => {
              if (act.APGroupActivityId !== actividadId) return act;
              const previousNames: string[] = (() => {
                if (Array.isArray((act as unknown as { evidence?: (string | File)[] }).evidence)) {
                  return (act as unknown as { evidence: (string | File)[] }).evidence.filter(
                    (e) => typeof e === 'string',
                  ) as string[];
                }
                const parsed = parsePhotos(act.GroupActivityPhotos);
                return parsed;
              })();
              const merged = Array.from(new Set([...previousNames, ...uploaded]));
              return {
                ...act,
                GroupActivityPhotos: JSON.stringify(merged),
                evidence: merged,
              };
            }) || [],
        }));
      }
    } catch (e) {
      console.error('Failed to upload evidence', e);
    }
  };

  const handleDeleteEvidencias = async (objetivoId: number, actividadId: number) => {
    setActivitiesByObjective((prev: Record<string, Activity[]>) => {
      const updated: Record<string, Activity[]> = { ...prev };
      if (objetivoId !== 0 && updated[objetivoId]) {
        updated[objetivoId] = updated[objetivoId].map((act) =>
          act.APGroupActivityId === actividadId
            ? { ...act, evidence: [], GroupActivityPhotos: '' }
            : act,
        );
      } else {
        Object.keys(updated).forEach((key) => {
          updated[key] = (updated[key] || []).map((act) =>
            act.APGroupActivityId === actividadId
              ? { ...act, evidence: [], GroupActivityPhotos: '' }
              : act,
          );
        });
      }
      return updated;
    });
    try {
      if (deleteActivityEvidence && currentFulfillmentVersion) {
        await (
          deleteActivityEvidence as unknown as (
            activityId: number,
            fulfillmentId?: number,
          ) => Promise<void>
        )(actividadId, currentFulfillmentVersion);
      }
    } catch (e) {
      console.error('Failed to delete evidence', e);
    }
  };

  useEffect(() => {
    if (!evidenceViewer.open && evidenceViewer.activityId) {
      setActivitiesByObjective((prev) => {
        const updated: typeof prev = { ...prev };
        Object.keys(updated).forEach((key) => {
          updated[key] = (updated[key] || []).map((act) =>
            act.APGroupActivityId === evidenceViewer.activityId &&
            !parsePhotos(act.GroupActivityPhotos).length
              ? { ...act, evidence: [], GroupActivityPhotos: '' }
              : act,
          );
        });
        return updated;
      });
    }
  }, [evidenceViewer.activityId, evidenceViewer.open]);

  const openEvidence = async (activityId: number) => {
    setLoadingEvidence(true);
    if (!currentFulfillmentVersion) return;
    try {
      const files = await getActivityEvidence(activityId, currentFulfillmentVersion);
      setEvidenceViewer({ open: true, activityId, files });
    } catch (e) {
      console.error('Failed to load evidence list', e);
      setEvidenceViewer({ open: true, activityId, files: [] });
    } finally {
      setLoadingEvidence(false);
    }
  };

  const closeEvidence = () => {
    setEvidenceViewer({ open: false, files: [] });
  };

  const handleDeleteSingleEvidence = async (fileName: string, fulfillmentId: number) => {
    if (!evidenceViewer.activityId) return;
    try {
      await deleteActivityEvidenceFile(evidenceViewer.activityId, fileName, fulfillmentId);
      setEvidenceViewer((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.name !== fileName),
      }));
      setActivitiesByObjective((prev) => {
        const updated: typeof prev = { ...prev };
        Object.keys(updated).forEach((k) => {
          updated[k] = (updated[k] || []).map((act: Activity) => {
            if (act.APGroupActivityId === evidenceViewer.activityId) {
              const names = parsePhotos(act.GroupActivityPhotos).filter((n) => n !== fileName);
              return {
                ...act,
                GroupActivityPhotos: JSON.stringify(names),
                evidence: names,
              };
            }
            return act;
          });
        });
        return updated;
      });
    } catch (e) {
      console.error('Failed to delete single evidence', e);
    }
  };

  const isAnyProgressOutOfRange = () => {
    for (const acts of Object.values(activitiesByObjective)) {
      for (const act of acts) {
        const val = getComplianceForActivity(act);
        if (val && (val < 0 || val > 100)) return true;
      }
    }
    if (complianceByActivity.some((c) => c.progress < 0 || c.progress > 100)) return true;
    return false;
  };

  const handleBack = () => {
    if (!currentFulfillmentVersion) return;
    if (isAnyProgressOutOfRange()) {
      setProgressError(t('enterCompliance.invalidProgressRange'));
      return;
    }
    if (complianceByActivity.length === 0) {
      navigate('/');
      return;
    }
    updateActivityProgress(complianceByActivity, currentFulfillmentVersion).then(() => {
      navigate('/');
    });
  };

  const handleContinue = () => {
    if (!currentFulfillmentVersion) return;
    if (isAnyProgressOutOfRange()) {
      setProgressError(t('enterCompliance.invalidProgressRange'));
      return;
    }
    if (readOnlyMode) {
      navigate('/checklist');
      return;
    }
    if (complianceByActivity.length === 0) {
      navigate(`/checklist`);
      return;
    }
    updateActivityProgress(complianceByActivity, currentFulfillmentVersion).then(() => {
      navigate(`/checklist`);
    });
  };

  const handleViewDetails = () => {
    navigate('/new-annual-plan', {
      state: { returnTo: '/enter-compliance' },
    });
  };

  const handleExit = () => {
    navigate('/');
  };

  const getRequirementsForCurrentFocus = (focusId: number) => {
    if (!requirements || requirements.length === 0) return [];

    const focusRequirements = requirements.find((req) => req.APFocusId === focusId);
    return focusRequirements ? focusRequirements.RequirementTypes : [];
  };

  const getRequirementNameById = (requirementId: number, focusId: number) => {
    if (!requirementId) return t('defineActivities.noApply');

    const currentFocusRequirements = getRequirementsForCurrentFocus(focusId);
    for (const reqType of currentFocusRequirements) {
      const req = reqType.requirements.find((r) => r.APRequirementId === requirementId);
      if (req) return req.Name;
    }
    return `${t('defineActivities.reqId')}: ${requirementId}`;
  };

  const hasObjectivesWithoutProgress = objectives.some((objetivo) => {
    const activities = activitiesByObjective[objetivo.APObjectiveId] || [];
    return activities.some((actividad) => {
      const edited = complianceByActivity.find(
        (item) => item.activityId === actividad.APGroupActivityId,
      );
      const currentProgress = edited?.progress ?? actividad.CompletionProgress;

      if (currentProgress === undefined && !edited && actividad.CompletionProgress === undefined) {
        return true;
      }

      if (currentProgress !== undefined && currentProgress > 0) {
        const localEvidence = (actividad as unknown as { evidence?: (string | File)[] }).evidence;
        const photoNames = parsePhotos(actividad.GroupActivityPhotos) || [];
        const evidenceNames = Array.isArray(localEvidence)
          ? (localEvidence.filter((e): e is string => typeof e === 'string') as string[])
          : [];
        const evidenceCount = new Set([...photoNames, ...evidenceNames]).size;
        if (evidenceCount === 0) {
          return true;
        }
      }

      if (currentProgress !== undefined && (currentProgress < 0 || currentProgress > 100)) {
        return true;
      }

      return false;
    });
  });
  const disableNext = (!readOnlyMode && hasObjectivesWithoutProgress) || !!progressError;
  const disableNextReason = React.useMemo(() => {
    if (progressError) return progressError;
    if (!readOnlyMode && hasObjectivesWithoutProgress) {
      return t('enterCompliance.disableNextReason');
    }
    return undefined;
  }, [progressError, readOnlyMode, hasObjectivesWithoutProgress, t]);

  const showResultsStep = React.useMemo(
    () =>
      shouldShowSelfAssessmentResultsStep(readOnlyMode, planValidation?.Approved, selectedYear),
    [planValidation?.Approved, readOnlyMode, selectedYear],
  );
  const handleGoToResults = React.useCallback(() => {
    if (!showResultsStep) return;
    navigate('/self-assessment-results');
  }, [navigate, showResultsStep]);

  const stepGridSize = showResultsStep ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6, md: 4 };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: { xs: 2, md: 4 } }}>
      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto', width: '100%' }}>
        {/* Breadcrumb */}

        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{
              pt: 2,
              px: { xs: 2, md: 3 },
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              rowGap: { xs: 2, md: 0 },
            }}
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid
                container
                spacing={2}
                sx={{
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  rowGap: { xs: 1.5, sm: 0 },
                }}
              >
                {/* Bloque Plan Anual */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Bloque Ámbito */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('common.self_assessment')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={PlanAnualIcon} alt="Plan Anual" style={{ width: 70, height: 70 }} />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper */}
          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">1</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 1
                      </Typography>
                      <Typography variant="caption">
                        {t('enterCompliance.enterComplianceData')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 2
                      </Typography>
                      <Typography variant="caption">
                        {t('enterCompliance.maturityLevelEvaluation')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    height: '100%',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #ccc',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">3</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step')} 3
                      </Typography>
                      <Typography variant="caption">
                        {t('enterCompliance.selfAssessmentAndSignature')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {showResultsStep && (
                <Grid size={stepGridSize}>
                  <Paper
                    elevation={0}
                    onClick={handleGoToResults}
                    sx={{
                      flex: 1,
                      bgcolor: '#f5f5f5',
                      color: '#999',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      height: '100%',
                      cursor: 'pointer',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size="auto">
                        <Grid
                          container
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            color: '#999',
                            fontWeight: 'bold',
                          }}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Grid size="auto">4</Grid>
                        </Grid>
                      </Grid>
                      <Grid size="auto">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('common.step')} 4
                        </Typography>
                        <Typography variant="caption">{t('ceoValidation.step4Caption')}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              {t('enterCompliance.enterCompliance')}
            </Typography>

            {/* Focos Estratégicos */}
            <Box sx={{ p: 2, borderRadius: 1, border: '1px solid #A5B2BB' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {t('annualPlanning.strategicFocuses')}
              </Typography>

              {focuses.map((focus, index) => (
                <Accordion
                  key={focus.APFocusId}
                  expanded={expandedFocus === focus.APFocusId}
                  onChange={handleFocusChange(focus.APFocusId)}
                  sx={{
                    mb: 2,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: '#F4F8F8',
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      minHeight: '40px !important',
                      height: '40px !important',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        margin: '8px 0 !important',
                      },
                      '&.Mui-expanded': {
                        minHeight: '40px !important',
                        height: '40px !important',
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '8px 0 !important',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {index + 1}. {focus.Name}
                    </Typography>
                  </AccordionSummary>
                  {expandedFocus === focus.APFocusId && (
                    <AccordionDetails sx={{ bgcolor: 'white', pt: 3, mx: { xs: 0, md: -1.75 } }}>
                      {(objectivesByFocus[focus.APFocusId] ?? []).length > 0 && (
                        <Box sx={{ p: 2, borderRadius: 1, border: '1px solid #A5B2BB' }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            {t('annualPlanning.objectives')}
                          </Typography>

                          {objectivesByFocus[focus.APFocusId]?.map((objetivo) => (
                            <Accordion
                              key={objetivo.APObjectiveId}
                              expanded={expandedObjective === objetivo.APObjectiveId}
                              onChange={handleObjectiveChange(objetivo.APObjectiveId)}
                              sx={{
                                mb: 2,
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{
                                  bgcolor: '#e3f9fd',
                                  borderRadius: 1,
                                  border: '1px solid #A5B2BB',
                                  minHeight: '40px !important',
                                  '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    margin: '12px 0 !important',
                                    py: 0.5,
                                  },
                                  '&.Mui-expanded': {
                                    minHeight: '40px !important',
                                  },
                                  '& .MuiAccordionSummary-content.Mui-expanded': {
                                    margin: '12px 0 !important',
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ pr: 2, wordBreak: 'break-word', lineHeight: 1.5 }}
                                >
                                  {objetivo.Name}
                                </Typography>
                              </AccordionSummary>
                              {expandedObjective === objetivo.APObjectiveId && (
                                <AccordionDetails
                                  sx={{ bgcolor: 'white', p: 2, mx: { xs: 0, md: -1.75 } }}
                                >
                                  {/* Tabla de Actividades */}
                                  {(activitiesByObjective[objetivo.APObjectiveId]?.length ?? 0) >
                                    0 && (
                                    <Box sx={{ mt: { xs: 2, md: 4 } }}>
                                      <Paper
                                        elevation={0}
                                        sx={{
                                          borderRadius: 1,
                                          p: 1,
                                          overflow: 'hidden',
                                          mb: 2,
                                          border: '1px solid #A5B2BB',
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                                          {t('defineObjectives.activitiesByObjective')}
                                        </Typography>
                                        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                                          <Table size="medium" sx={{ minWidth: 720 }}>
                                            <TableHead>
                                              <TableRow sx={{ bgcolor: 'transparent' }}>
                                                {[
                                                  t('ceoValidation.id'),
                                                  t('ceoValidation.activity'),
                                                  t('ceoValidation.deadline'),
                                                  t('ceoValidation.activityType'),
                                                  t('ceoValidation.requirement'),
                                                  t('ceoValidation.progressLevel'),
                                                  t('ceoValidation.evidence'),
                                                ].map((label, idx) => (
                                                  <TableCell
                                                    key={label}
                                                    align={idx === 5 ? 'center' : 'left'}
                                                    sx={{
                                                      p: 0.25,
                                                      borderBottom: 'none',
                                                      bgcolor: 'transparent',
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        bgcolor: '#DEEAEB',
                                                        border: `1px solid #d5e2e7`,
                                                        px: 1,
                                                        py: 1,
                                                        height: 48,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderTopLeftRadius: 8,
                                                        borderTopRightRadius: 8,
                                                        boxShadow:
                                                          'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.06)',
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                          fontWeight: 600,
                                                          color: '#333',
                                                          lineHeight: 1.2,
                                                          whiteSpace: 'nowrap',
                                                        }}
                                                      >
                                                        {label}
                                                      </Typography>
                                                    </Box>
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(
                                                activitiesByObjective[objetivo.APObjectiveId] ?? []
                                              ).map((actividad, index, arr) => {
                                                const rowBg =
                                                  index % 2 === 0 ? '#EFF5F5' : '#FAFCFC';
                                                const isLastRow = index === arr.length - 1;
                                                const innerCommon = {
                                                  border: `1px solid #e1eaee`,
                                                  px: 1,
                                                  py: { xs: 1.5, md: 1 },
                                                  bgcolor: rowBg,
                                                  boxShadow:
                                                    'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 1px rgba(0,0,0,0.04)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  minHeight: 64,
                                                  height: '100%',
                                                  ...(isLastRow && {
                                                    borderBottomLeftRadius: 8,
                                                    borderBottomRightRadius: 8,
                                                  }),
                                                } as const;

                                                const requirementName = actividad.ApplyRequirement
                                                  ? getRequirementNameById(
                                                      actividad.APRequirementId,
                                                      focus.APFocusId,
                                                    )
                                                  : t('defineActivities.noApply');

                                                const localEvidence = (
                                                  actividad as unknown as {
                                                    evidence?: (string | File)[];
                                                  }
                                                ).evidence;
                                                const photoNames = parsePhotos(
                                                  actividad.GroupActivityPhotos,
                                                );
                                                const evidenceNames = Array.isArray(localEvidence)
                                                  ? (localEvidence.filter(
                                                      (e): e is string => typeof e === 'string',
                                                    ) as string[])
                                                  : [];
                                                const evidenceCount = new Set([
                                                  ...photoNames,
                                                  ...evidenceNames,
                                                ]).size;

                                                return (
                                                  <TableRow
                                                    key={actividad.APGroupActivityId}
                                                    sx={{ bgcolor: 'transparent' }}
                                                  >
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box sx={innerCommon}>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            fontSize: '0.875rem',
                                                          }}
                                                        >
                                                          {String(index + 1).padStart(2, '0')}
                                                        </Typography>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box sx={innerCommon}>
                                                        <Tooltip
                                                          title={actividad.GroupActivityName || ''}
                                                          arrow
                                                          placement="top"
                                                        >
                                                          <Typography
                                                            variant="body2"
                                                            textAlign={'center'}
                                                            sx={{
                                                              fontSize: '0.875rem',
                                                              overflow: 'hidden',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              maxWidth: { xs: 160, md: 200 },
                                                              cursor: 'default',
                                                            }}
                                                          >
                                                            {actividad.GroupActivityName &&
                                                            actividad.GroupActivityName.length > 120
                                                              ? `${actividad.GroupActivityName.substring(0, 120)}...`
                                                              : actividad.GroupActivityName || ''}
                                                          </Typography>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box sx={innerCommon}>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            fontSize: '0.875rem',
                                                          }}
                                                        >
                                                          {actividad.StartDate
                                                            ? new Date(
                                                                actividad.StartDate,
                                                              ).toLocaleDateString()
                                                            : '-'}{' '}
                                                          {t('pdf.separator')}{' '}
                                                          {actividad.EndDate
                                                            ? new Date(
                                                                actividad.EndDate,
                                                              ).toLocaleDateString()
                                                            : '-'}
                                                        </Typography>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          ...innerCommon,
                                                          justifyContent: 'center',
                                                        }}
                                                      >
                                                        <Box
                                                          sx={{
                                                            bgcolor: '#F4F8F8',
                                                            border: '1px solid #A5B2BB',
                                                            px: 1.5,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            fontSize: '0.8125rem',
                                                            minHeight: 30,
                                                            fontWeight: 400,
                                                            textAlign: 'center',
                                                            borderRadius: 1,
                                                          }}
                                                        >
                                                          {actividad.GroupActivityType ===
                                                          ActivityTypes.STRATEGIC
                                                            ? t('defineActivities.strategic')
                                                            : t('defineActivities.permanent')}
                                                        </Box>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box sx={innerCommon}>
                                                        <Tooltip
                                                          title={requirementName}
                                                          arrow
                                                          placement="top"
                                                        >
                                                          <Typography
                                                            variant="body2"
                                                            textAlign={'center'}
                                                            sx={{
                                                              fontSize: '0.875rem',
                                                              overflow: 'hidden',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              maxWidth: { xs: 160, md: 200 },
                                                              cursor: 'default',
                                                            }}
                                                          >
                                                            {requirementName.length > 120
                                                              ? `${requirementName.substring(0, 120)}...`
                                                              : requirementName}
                                                          </Typography>
                                                        </Tooltip>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Grid
                                                        container
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={innerCommon}
                                                      >
                                                        <Grid size="auto">
                                                          <TextField
                                                            required
                                                            size="small"
                                                            type="number"
                                                            placeholder={'0-100'}
                                                            disabled={readOnlyMode ?? false}
                                                            value={getComplianceForActivity(
                                                              actividad,
                                                            )}
                                                            onChange={(e) =>
                                                              handleProgressChange(
                                                                actividad.APGroupActivityId,
                                                                e.target.value,
                                                              )
                                                            }
                                                            inputProps={{
                                                              min: 0,
                                                              max: 100,
                                                            }}
                                                            error={!!progressError}
                                                            sx={{ width: 80 }}
                                                          />
                                                        </Grid>
                                                        <Grid size="auto">
                                                          <Typography variant="body2">%</Typography>
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                    <TableCell
                                                      sx={{ p: 0.25, borderBottom: 'none' }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          ...innerCommon,
                                                          minWidth: 120,
                                                          gap: { xs: 2, sm: 2 },
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                        }}
                                                      >
                                                        <Box>
                                                          {evidenceCount > 0 ? (
                                                            <Badge
                                                              badgeContent={evidenceCount}
                                                              color="primary"
                                                            >
                                                              <IconButton
                                                                component="label"
                                                                size="small"
                                                                disabled={readOnlyMode ?? false}
                                                                sx={{
                                                                  bgcolor: '#00BCE4',
                                                                  color: 'white',
                                                                  '&:hover': {
                                                                    bgcolor: '#00acc1',
                                                                  },
                                                                  width: 40,
                                                                  height: 40,
                                                                }}
                                                              >
                                                                <AttachFile fontSize="small" />
                                                                <input
                                                                  type="file"
                                                                  hidden
                                                                  accept="image/*,.pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.heic,.heif,.zip,.rar"
                                                                  multiple
                                                                  onChange={(e) =>
                                                                    handleFileUpload(
                                                                      objetivo.APObjectiveId,
                                                                      actividad.APGroupActivityId,
                                                                      e.target.files,
                                                                    )
                                                                  }
                                                                />
                                                              </IconButton>
                                                            </Badge>
                                                          ) : (
                                                            <IconButton
                                                              component="label"
                                                              size="small"
                                                              sx={{
                                                                bgcolor: '#00BCE4',
                                                                color: 'white',
                                                                '&:hover': {
                                                                  bgcolor: '#00acc1',
                                                                },
                                                                width: 40,
                                                                height: 40,
                                                              }}
                                                            >
                                                              <AttachFile fontSize="small" />
                                                              <input
                                                                type="file"
                                                                hidden
                                                                accept="image/*,.pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.heic,.heif,.zip,.rar"
                                                                multiple
                                                                onChange={(e) =>
                                                                  handleFileUpload(
                                                                    objetivo.APObjectiveId,
                                                                    actividad.APGroupActivityId,
                                                                    e.target.files,
                                                                  )
                                                                }
                                                              />
                                                            </IconButton>
                                                          )}
                                                        </Box>
                                                        {evidenceCount > 0 && (
                                                          <IconButton
                                                            onClick={() =>
                                                              openEvidence(
                                                                actividad.APGroupActivityId,
                                                              )
                                                            }
                                                            disabled={isAnyLoading}
                                                            size="small"
                                                            sx={{
                                                              bgcolor: '#1976d2',
                                                              color: 'white',
                                                              '&:hover': {
                                                                bgcolor: '#1565c0',
                                                              },
                                                              width: 40,
                                                              height: 40,
                                                            }}
                                                          >
                                                            <Visibility fontSize="small" />
                                                          </IconButton>
                                                        )}
                                                      </Box>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>

                                        {/* Información de registros - Solo mostrar si hay actividades */}
                                        {(activitiesByObjective[objetivo.APObjectiveId]?.length ??
                                          0) > 0 && (
                                          <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                              {t('enterCompliance.totalActivities')}:{' '}
                                              {activitiesByObjective[objetivo.APObjectiveId]
                                                ?.length ?? 0}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Paper>
                                    </Box>
                                  )}
                                </AccordionDetails>
                              )}
                            </Accordion>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  )}
                </Accordion>
              ))}
            </Box>

            {/* Botones de navegación */}
            <Navigation
              onSaveDraft={handleBack}
              saveDraftTitle={t('enterCompliance.saveProgress')}
              onBack={readOnlyMode ? handleViewDetails : undefined}
              backTitle={readOnlyMode ? t('common.viewDetails') : undefined}
              disableNext={disableNext}
              disableReason={disableNextReason}
              onNext={handleContinue}
              nextTitle={
                readOnlyMode ? t('common.continue') : t('enterCompliance.saveAndContinueStep2')
              }
              readOnlyMode={readOnlyMode}
              onExit={() => handleExit()}
              exitTitle={t('common.exit')}
              isAnyLoading={isAnyLoading}
              disableSaveDraft={false}
            />
          </Box>
        </Paper>
      </Box>

      {/* Modal de confirmación al inicio de autoevaluación */}
      <Dialog
        open={showConfirmationModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 0,
            pt: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('enterCompliance.selfAssessmentStartTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              bgcolor: '#e8f5f5',
              borderRadius: 1,
              p: 3,
              mb: 2,
              border: '1px solid #00BCE4',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircle sx={{ color: '#00BCE4', mr: 2, fontSize: 32 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#00838f' }}>
                {t('enterCompliance.selfAssessmentAboutToStart')}
              </Typography>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
              {t('enterCompliance.needChangeDefinitionsQuestion')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
              {definitionsWarningText}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
            px: 4,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading || !definitionWindowActive}
            onClick={() => {
              if (currentVersion && reopenPlanVersion) {
                Promise.resolve(reopenPlanVersion(currentVersion))
                  .then((res: unknown) => {
                    if (res && typeof res === 'object' && 'newVersionId' in res) {
                      const newVersionId = (res as { newVersionId?: number }).newVersionId;
                      if (typeof newVersionId === 'number' && setCurrentVersion) {
                        setCurrentVersion(newVersionId);
                      }
                    }
                  })
                  .finally(() => {
                    navigate('/');
                  });
              } else {
                navigate('/');
              }
            }}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 180,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('enterCompliance.goBackEditDefinitions')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading}
            onClick={() => {
              if (currentVersion) {
                createFulfillmentVersion(currentVersion);
              }
              setShowConfirmationModal(false);
            }}
            sx={{
              bgcolor: '#00BCE4',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 180,
              '&:hover': { bgcolor: '#00acc1' },
            }}
            endIcon={<ArrowForward />}
          >
            {t('enterCompliance.continueWithSelfAssessment')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal visor de evidencias */}
      <Dialog open={evidenceViewer.open} onClose={closeEvidence} fullWidth maxWidth="md">
        <DialogTitle>
          {t('enterCompliance.evidenceDialogTitle', { id: evidenceViewer.activityId ?? '' })}
        </DialogTitle>
        <DialogContent dividers>
          {loadingEvidence && <Typography variant="body2">{t('common.loading')}</Typography>}
          {!loadingEvidence && evidenceViewer.files.length === 0 && (
            <Typography variant="body2">{t('enterCompliance.noEvidence')}</Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {evidenceViewer.files.map((f) => {
              const lower = f.name.toLowerCase();
              const isImage = /(png|jpg|jpeg|gif|webp|bmp|svg|heic|heif)$/.test(lower);
              const isPdf = /\.pdf$/.test(lower);
              const isExcel = /\.(xls|xlsx|csv)$/.test(lower);
              const isDoc = /\.(doc|docx|ppt|pptx)$/.test(lower);
              const isArchive = /\.(zip|rar)$/.test(lower);
              const extLabel = lower.split('.').pop();
              return (
                <Grid key={f.name} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      position: 'relative',
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={(readOnlyMode ?? false) || isAnyLoading}
                      onClick={() => {
                        if (currentFulfillmentVersion != null) {
                          handleDeleteSingleEvidence(f.name, currentFulfillmentVersion);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        zIndex: 2,
                        bgcolor: 'rgba(244,67,54,0.15)',
                        color: '#f44336',
                        '&:hover': { bgcolor: 'rgba(244,67,54,0.3)' },
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                    {isImage && (
                      <Box
                        sx={{
                          position: 'relative',
                          pt: '56%',
                          overflow: 'hidden',
                          borderRadius: 1,
                          bgcolor: '#fafafa',
                          border: '1px solid #eee',
                        }}
                      >
                        <img
                          src={f.url}
                          alt={f.name}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )}
                    {isPdf && (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#fafafa',
                          border: '1px solid #eee',
                          borderRadius: 1,
                          fontSize: 24,
                          fontWeight: 600,
                          color: '#d32f2f',
                          position: 'relative',
                        }}
                      >
                        PDF
                      </Box>
                    )}
                    {isExcel && (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#eefaf4',
                          border: '1px solid #d2e8da',
                          borderRadius: 1,
                          fontSize: 22,
                          fontWeight: 600,
                          color: '#2e7d32',
                        }}
                      >
                        XLS
                      </Box>
                    )}
                    {isDoc && (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#eef2fb',
                          border: '1px solid #d6def0',
                          borderRadius: 1,
                          fontSize: 22,
                          fontWeight: 600,
                          color: '#1565c0',
                        }}
                      >
                        DOC
                      </Box>
                    )}
                    {isArchive && (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#eef2fb',
                          border: '1px solid #d6def0',
                          borderRadius: 1,
                          fontSize: 22,
                          fontWeight: 600,
                          color: '#6d4c41',
                        }}
                      >
                        ZIP/RAR
                      </Box>
                    )}
                    {!isImage && !isPdf && !isExcel && !isDoc && !isArchive && (
                      <Box
                        sx={{
                          height: 140,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f5f5f5',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          fontSize: 20,
                          fontWeight: 600,
                          color: '#555',
                        }}
                      >
                        {extLabel?.toUpperCase() || 'FILE'}
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ flex: 1, pr: 1, wordBreak: 'break-all' }}>
                        {f.name}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isAnyLoading}
                        onClick={() => window.open(f.url, '_blank')}
                      >
                        {t('common.open')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            color="error"
            disabled={evidenceViewer.files.length === 0 || (readOnlyMode ?? false) || isAnyLoading}
            onClick={() => {
              if (!evidenceViewer.activityId) return;
              handleDeleteEvidencias(0, evidenceViewer.activityId);
              setEvidenceViewer((prev) => ({ ...prev, files: [] }));
            }}
          >
            {t('enterCompliance.deleteAllEvidence')}
          </Button>
          <Button onClick={closeEvidence}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnterCompliance;
